import apiClient from "@/lib/api/api-client";
import type { ExpectedDailyVolume } from "@/lib/api/login-user";

export type ApiPlatform = "glofox";
export type RequesterType = "customer" | "partner";
export type AccessRequestStatus = "pending" | "approved" | "rejected";
export type AccessScope = "namespaces" | "locations";
/** Key environment for integrator access (Task 5+). Optional on legacy rows. */
export type KeyEnvironment = "live" | "sandbox";

export interface AccessRequest {
  id: string;
  portal_user_id: string;
  integrator_name?: string;
  api_platform: ApiPlatform;
  requester_type: RequesterType;
  intended_use_case: string;
  /** Present when Task 5+ persists environment; omitted on legacy requests. */
  environment?: KeyEnvironment | null;
  access_scope?: AccessScope | null;
  location_ids: string[];
  location_names?: string[];
  namespaces: string[];
  expected_daily_volume: ExpectedDailyVolume | null;
  integrations: string[];
  status: AccessRequestStatus;
  /** Applicant-visible reason when status is rejected. */
  rejection_reason?: string | null;
  created_at: string;
  updated_at: string;
  /** Newest-first persisted Request activity. Omitted on older API responses. */
  activity?: AccessRequestActivity[];
}

export interface AccessRequestActivity {
  type: string;
  created_at: string;
}

export interface CreateAccessRequestPayload {
  api_platform: ApiPlatform;
  /** Legacy Request Access (Slice 6 off). Optional when Slice 6 uses access_role. */
  requester_type?: RequesterType;
  access_scope: AccessScope;
  intended_use_case: string;
  location_ids: string[];
  namespaces: string[];
  expected_daily_volume: ExpectedDailyVolume;
  integrations: string[];
}

/**
 * Integrator request payload (Sandbox/Live). Sandbox must omit every scope
 * field; Live requires `access_scope` plus the matching list.
 */
export interface CreateIntegratorRequestPayload {
  api_platform: ApiPlatform;
  integrator_name: string;
  environment: KeyEnvironment;
  requester_type?: RequesterType;
  intended_use_case: string;
  access_scope?: AccessScope;
  namespaces?: string[];
  location_names?: string[];
}

export interface CreateAccessRequestResponse {
  request: AccessRequest;
  message: string;
}

export interface ListAccessRequestsResponse {
  requests: AccessRequest[];
}

export interface RequestZapierIntegrationResponse {
  request: AccessRequest;
  message: string;
}

const ACCESS_REQUESTS_PATH = "/api/v1/access-requests";

export async function listAccessRequests(): Promise<ListAccessRequestsResponse> {
  const { data } = await apiClient.get<ListAccessRequestsResponse>(
    ACCESS_REQUESTS_PATH,
  );
  return data;
}

export async function createAccessRequest(
  payload: CreateAccessRequestPayload,
): Promise<CreateAccessRequestResponse> {
  const { data } = await apiClient.post<CreateAccessRequestResponse>(
    ACCESS_REQUESTS_PATH,
    payload,
  );
  return data;
}

export async function createIntegratorRequest(
  payload: CreateIntegratorRequestPayload,
): Promise<CreateAccessRequestResponse> {
  const { data } = await apiClient.post<CreateAccessRequestResponse>(
    ACCESS_REQUESTS_PATH,
    payload,
  );
  return data;
}

export async function requestZapierIntegration(
  accessRequestId: string,
): Promise<RequestZapierIntegrationResponse> {
  const { data } = await apiClient.post<RequestZapierIntegrationResponse>(
    `${ACCESS_REQUESTS_PATH}/${accessRequestId}/integrations/zapier`,
  );
  return data;
}
